'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import Link from 'next/link';
import Image from 'next/image';
import { motion } from 'framer-motion';
import { CaretDown, Check, Funnel, ArrowsDownUp } from '@phosphor-icons/react';
import ErrorBoundary from '@/components/error-boundary';
import { NavBarMain } from '@/components/seasons/NavBarMain';
import { Footer } from '@/components/seasons/Footer';
import { FlowNavBar } from '@/components/seasons/FlowNavBar';
import { FlowFooter } from '@/components/seasons/FlowFooter';
import { CometNavBar } from '@/components/seasons/CometNavBar';
import { CometFooter } from '@/components/seasons/CometFooter';
import { GalaxyNavBar } from '@/components/seasons/GalaxyNavBar';
import { GalaxyFooter } from '@/components/seasons/GalaxyFooter';
import { GravityNavBar } from '@/components/seasons/GravityNavBar';
import { GravityFooter } from '@/components/seasons/GravityFooter';
import { StoryhealthNavBar } from '@/components/seasons/StoryhealthNavBar';
import { StoryhealthFooter } from '@/components/seasons/StoryhealthFooter';
import { SimpleButton } from '@/components/seasons/SimpleButton';
import { PdfThumbnail } from '@/components/seasons/PdfThumbnail';
import { useClickOutside } from '@/lib/seasons/useClickOutside';
import { useCtrlPanelPosition } from '@/lib/seasons/useCtrlPanelPosition';
import type { BlogSettingsData } from '@/lib/blog-settings';
import type { PostSummary } from './page';
import { toEditorial, NEWSROOM_CATEGORY } from '@/lib/editorial-categories';
import { brandExternalUrl } from '@/lib/brand-urls';

const MotionLink = motion(Link);

const EASE_OUT: [number, number, number, number] = [0, 0, 0.2, 1];
const fadeUp = { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { duration: 0.4, ease: EASE_OUT } } };
const stagger = { visible: { transition: { staggerChildren: 0.07 } } };

const PAGE_SIZE = 9;

// Fixed editorial tab categories (Newsroom lives at /news, not as a tab here)
const EDITORIAL_CATEGORIES = ['All', 'Insights', 'Spotlight', 'Product', 'Conversations'];

// Canonical raw categories ordered by volume (from Innovaccer_Blogs_Categorized.xlsx)
const CANONICAL_CATEGORIES = [
  'Data & Interoperability',
  'AI',
  'Value-Based Care',
  'Industry & Company',
  'Population Health',
  'Patient Engagement',
  'Care Management',
  'Policy & Compliance',
  'Clinical Documentation',
  'RCM',
  'Prior Authorization',
  'Risk Adjustment',
  'Denial Management',
  'Autonomous Coding',
];

// Display labels for canonical categories — only expanded forms differ from the
// raw value. Data values stay as 'AI' / 'RCM' so post-frontmatter filtering
// keeps working; we just render the expanded form to the user (no abbreviation
// prefix — full words only).
const TOPIC_DISPLAY_LABEL: Record<string, string> = {
  AI: 'Artificial Intelligence',
  RCM: 'Revenue Cycle Management',
};
function topicLabel(topic: string): string {
  return TOPIC_DISPLAY_LABEL[topic] ?? topic;
}

const SORT_OPTIONS: { label: string; value: 'newest' | 'oldest' }[] = [
  { label: 'Newest first', value: 'newest' },
  { label: 'Oldest first', value: 'oldest' },
];

const VISIBLE_BRANDS = new Set(['innovaccer', 'gravity']);

const BRANDS: { value: string; label: string }[] = [
  { value: 'innovaccer', label: 'Innovaccer' },
  { value: 'gravity',    label: 'Gravity' },
];

interface Props {
  posts: PostSummary[];
  searchPosts?: PostSummary[];
  blogSettings?: BlogSettingsData | null;
  /** 'flow' or 'comet' render brand nav/footer, hide the brand filter,
      scope posts to that brand, and route under /<brand>/blogs. */
  variant?: 'innovaccer' | 'flow' | 'comet' | 'galaxy' | 'gravity' | 'storyhealth';
}

// ─── Category slug maps (needed by both tagHref and handleCategoryClick) ─────

const CATEGORY_TO_SLUG: Record<string, string> = {
  'All': '',
  'Insights': 'insights',
  'Spotlight': 'spotlight',
  'Customers': 'customers',
  'Product': 'product',
  'Conversations': 'conversations',
  'Case Study': 'case-study',
  [NEWSROOM_CATEGORY]: 'newsroom',
  // Story Health's admin-style public tabs (see ADMIN_STYLE_TABS below)
  'Brochures': 'brochures',
  'Case Studies': 'case-studies',
  'Industry Reports': 'industry-reports',
  'Stories': 'stories',
  'White Papers': 'white-papers',
  'Webinars': 'webinars',
};

/** Story Health's public /resources listing mirrors its own admin dashboard's
 * tab set (All/Brochures/Case Studies/Industry Reports/Stories/White Papers/
 * Newsroom/Webinars) instead of the blog-editorial tabs other brands use —
 * since it lists genuine resource-type posts (Case Study, etc.) alongside
 * Stories on the same page. */
const ADMIN_STYLE_TABS = ['All', 'Brochures', 'Case Studies', 'Industry Reports', 'Stories', 'White Papers', 'Newsroom', 'Webinars'];
const RESOURCE_CATEGORY_TO_TAB: Record<string, string> = {
  'White Paper': 'White Papers',
  'Case Study': 'Case Studies',
  'Industry Report': 'Industry Reports',
  'Brochure': 'Brochures',
};
function adminStyleTabFor(post: PostSummary): string {
  if (post.category === NEWSROOM_CATEGORY) return 'Newsroom';
  if (post.category && RESOURCE_CATEGORY_TO_TAB[post.category]) return RESOURCE_CATEGORY_TO_TAB[post.category];
  return 'Stories';
}
const SLUG_TO_CATEGORY: Record<string, string> = Object.fromEntries(
  Object.entries(CATEGORY_TO_SLUG).map(([k, v]) => [v, k])
);

// ─── Resource URL + type helpers ─────────────────────────────────────────────

const RESOURCE_URL_SEGMENT: Record<string, string> = {
  'White Paper':     'white-papers',
  'Case Study':      'case-studies',
  'Industry Report': 'industry-reports',
  'Brochure':        'brochures',
};

const RESOURCE_CATEGORIES = new Set(Object.keys(RESOURCE_URL_SEGMENT));

function postHref(post: PostSummary): string {
  if (post.newsLink) return post.newsLink;
  const external = brandExternalUrl(post.brand, post.slug, post.category);
  if (external) return external;
  if (post.category && RESOURCE_CATEGORIES.has(post.category)) {
    const seg = RESOURCE_URL_SEGMENT[post.category];
    return `/resources/${seg}/${post.slug}`;
  }
  if (post.category === NEWSROOM_CATEGORY) return `/news/${post.slug}`;
  return `/blogs/${post.slug}`;
}

/** URL of the listing page the type tag should link to. */
function tagHref(post: PostSummary): string {
  if (post.category && RESOURCE_CATEGORIES.has(post.category)) {
    return `/resources/${RESOURCE_URL_SEGMENT[post.category]}`;
  }
  // Blog editorial — use contentType or fall back to category
  const editorial = post.contentType || post.category || '';
  const slug = CATEGORY_TO_SLUG[editorial];
  if (slug) return `/blogs?category=${slug}`;
  return '/blogs';
}

/** Human-readable type label for a post, shown as a tag in search results. */
function typeLabel(post: PostSummary): string {
  if (post.category === 'White Paper')     return 'Whitepaper';
  if (post.category === 'Case Study')      return 'Case Study';
  if (post.category === 'Industry Report') return 'Industry Report';
  if (post.category === 'Brochure')        return 'Brochure';
  // Blog: use contentType (editorial category)
  if (post.contentType) return post.contentType;
  return 'Story';
}

function formatDate(iso: string) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function readMinutesFor(post: PostSummary): number {
  return post.readMinutes || 0;
}

function MetaSep() {
  return <span className="bl-sep" aria-hidden="true" />;
}

function imageFor(post: PostSummary) {
  return post.heroImg || `https://picsum.photos/seed/blog-${post.slug}/800/500`;
}

function authorLabel(post: PostSummary): string | null {
  if (!post.authorName) return null;
  const extra = (post.authorCount ?? 1) - 1;
  return extra > 0 ? `${post.authorName} +${extra}` : post.authorName;
}

/** Story type chip (Insights / Customers / Product / Conversations / Newsroom
 * — or, for genuine resource-type posts like Case Study/White Paper, the
 * category itself, matching the admin dashboard's own card labels). */
function storyTypeLabel(post: PostSummary): string | null {
  if (post.category === 'White Paper') return 'Whitepaper';
  if (post.category && RESOURCE_CATEGORIES.has(post.category)) return post.category;
  const editorial = toEditorial(post.category || '', post.contentType);
  if (!editorial || editorial === 'All') return null;
  return editorial;
}

/** Genuine resource-type posts (Case Study, White Paper, etc.) show the same
 * fanned PDF-page thumbnail used everywhere else for that content — matching
 * the admin dashboard's own card treatment — instead of a plain hero image. */
function isResourceType(post: PostSummary): boolean {
  return !!post.category && RESOURCE_CATEGORIES.has(post.category);
}

function PostCardStandard({ post }: { post: PostSummary }) {
  const storyType = storyTypeLabel(post);
  const isResource = isResourceType(post);
  return (
    <MotionLink href={postHref(post)} prefetch={false} className="bl-post-card" variants={fadeUp} {...(postHref(post).startsWith('https://') ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
      <div className="bl-post-card__media" style={isResource ? undefined : { backgroundImage: `url(${imageFor(post)})` }}>
        {isResource
          ? <PdfThumbnail pdfUrl={post.pdfUrl} heroImg={post.heroImg} />
          : <Image fill className="bl-post-card__img" src={imageFor(post)} alt={post.title} style={{ objectFit: 'cover', zIndex: 1 }} sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw" />}
      </div>
      <div className="bl-post-card__body">
        <h3 className="type-heading-md bl-post-card__title">{post.title}</h3>
        <div className="bl-post-card__meta">
          {storyType && <><span className="bl-post-card__meta-text">{storyType}</span><MetaSep /></>}
          <span className="bl-post-card__meta-text">{formatDate(post.date || '')}</span>
          {!!readMinutesFor(post) && (
            <>
              <MetaSep />
              <span className="bl-post-card__meta-text">{readMinutesFor(post)} min read</span>
            </>
          )}
        </div>
      </div>
    </MotionLink>
  );
}

function StackItem({ post }: { post: PostSummary }) {
  const storyType = storyTypeLabel(post);
  const isResource = isResourceType(post);
  return (
    <Link href={postHref(post)} prefetch={false} className="bl-post-card" {...(postHref(post).startsWith('https://') ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
      <div className="bl-post-card__media" style={isResource ? undefined : { backgroundImage: `url(${imageFor(post)})` }}>
        {isResource
          ? <PdfThumbnail pdfUrl={post.pdfUrl} heroImg={post.heroImg} />
          : <Image fill className="bl-post-card__img" src={imageFor(post)} alt={post.title} style={{ objectFit: 'cover', zIndex: 1 }} sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw" />}
      </div>
      <div className="bl-post-card__body">
        <h3 className="type-heading-md bl-post-card__title">{post.title}</h3>
        <div className="bl-post-card__meta">
          {storyType && <><span className="bl-post-card__meta-text">{storyType}</span><MetaSep /></>}
          <span className="bl-post-card__meta-text">{formatDate(post.date || '')}</span>
          {!!readMinutesFor(post) && (
            <>
              <MetaSep />
              <span className="bl-post-card__meta-text">{readMinutesFor(post)} min read</span>
            </>
          )}
        </div>
      </div>
    </Link>
  );
}

function FeaturedCluster({ featured, stack }: { featured: PostSummary; stack: PostSummary[] }) {
  const author = authorLabel(featured);
  return (
    <div className="bl-efc">
      <Link href={postHref(featured)} className="bl-efc__main" aria-label={featured.title} {...(postHref(featured).startsWith('https://') ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
        <div className="bl-efc__main-media" style={{ backgroundImage: `url(${imageFor(featured)})` }}>
          <Image fill src={imageFor(featured)} alt="" style={{ objectFit: 'cover', zIndex: 1 }} sizes="(max-width: 768px) 100vw, 60vw" priority />
        </div>
        <div className="bl-efc__main-body">
          <h2 className="type-display-md bl-efc__main-title">{featured.title}</h2>
          <div className="bl-efc__main-footer">
            {author && <><span className="bl-post-card__meta-text">{author}</span><MetaSep /></>}
            <span className="bl-post-card__meta-text">{formatDate(featured.date || '')}</span>
            {!!readMinutesFor(featured) && (
              <>
                <MetaSep />
                <span className="bl-post-card__meta-text">{readMinutesFor(featured)} min read</span>
              </>
            )}
          </div>
        </div>
      </Link>
      <div className="bl-efc__stack">
        {stack.map((post) => (
          <StackItem key={post.slug} post={post} />
        ))}
      </div>
    </div>
  );
}

function FilterDropdown({ topics, selected, onChange, label = 'Filter' }: { topics: string[]; selected: string[]; onChange: (v: string[]) => void; label?: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  useClickOutside(ref, () => setOpen(false));
  const fixedPos = useCtrlPanelPosition(open, triggerRef);
  const hasActive = selected.length > 0;
  function toggle(t: string) {
    onChange(selected.includes(t) ? selected.filter((x) => x !== t) : [...selected, t]);
  }
  return (
    <div className="bl-ctrl" ref={ref}>
      <button ref={triggerRef} type="button" className={`bl-ctrl__trigger${hasActive ? ' bl-ctrl__trigger--active' : ''}`} onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <Funnel size={13} weight={hasActive ? 'fill' : 'regular'} />
        {label}
        {hasActive && <span className="bl-ctrl__badge">{selected.length}</span>}
        <CaretDown size={12} weight="bold" />
      </button>
      {open && (
        <div className="bl-ctrl__panel" role="listbox" aria-multiselectable="true" style={fixedPos ? { position: 'fixed', top: fixedPos.top, left: fixedPos.left, right: 'auto' } : undefined}>
          {topics.map((topic) => {
            const on = selected.includes(topic);
            return (
              <button key={topic} type="button" className={`bl-ctrl__option${on ? ' bl-ctrl__option--selected' : ''}`} onClick={() => toggle(topic)} role="option" aria-selected={on}>
                <span className={`bl-ctrl__check${on ? ' bl-ctrl__check--on' : ''}`}>{on && <Check size={10} weight="bold" color="white" />}</span>
                {topicLabel(topic)}
              </button>
            );
          })}
          {hasActive && (
            <div className="bl-ctrl__footer">
              <button type="button" className="bl-ctrl__clear" onClick={() => onChange([])}>Clear all</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SortDropdown({ value, onChange }: { value: 'newest' | 'oldest'; onChange: (v: 'newest' | 'oldest') => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  useClickOutside(ref, () => setOpen(false));
  const fixedPos = useCtrlPanelPosition(open, triggerRef);
  const label = SORT_OPTIONS.find((o) => o.value === value)?.label ?? 'Sort';
  return (
    <div className="bl-ctrl" ref={ref}>
      <button ref={triggerRef} type="button" className="bl-ctrl__trigger" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <ArrowsDownUp size={13} />
        {label}
        <CaretDown size={12} weight="bold" />
      </button>
      {open && (
        <div className="bl-ctrl__panel bl-ctrl__panel--right" style={fixedPos ? { position: 'fixed', top: fixedPos.top, left: fixedPos.left, right: 'auto' } : undefined}>
          {SORT_OPTIONS.map((o) => (
            <button key={o.value} type="button" className={`bl-ctrl__option${value === o.value ? ' bl-ctrl__option--selected' : ''}`} onClick={() => { onChange(o.value); setOpen(false); }}>
              <span className="bl-ctrl__check-slot">{value === o.value && <Check size={11} weight="bold" />}</span>
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function BrandDropdown({ selected, onChange }: { selected: string[]; onChange: (v: string[]) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  useClickOutside(ref, () => setOpen(false));
  const fixedPos = useCtrlPanelPosition(open, triggerRef);
  const hasActive = selected.length > 0;

  function toggle(v: string) {
    onChange(selected.includes(v) ? selected.filter(x => x !== v) : [...selected, v]);
  }

  return (
    <div className="bl-ctrl" ref={ref}>
      <button ref={triggerRef} type="button" className={`bl-ctrl__trigger${hasActive ? ' bl-ctrl__trigger--active' : ''}`} onClick={() => setOpen(o => !o)} aria-expanded={open}>
        Product
        {hasActive && <span className="bl-ctrl__badge">{selected.length}</span>}
        <CaretDown size={12} weight="bold" />
      </button>
      {open && (
        <div className="bl-ctrl__panel" role="listbox" aria-multiselectable="true" style={fixedPos ? { position: 'fixed', top: fixedPos.top, left: fixedPos.left, right: 'auto' } : undefined}>
          <div className="bl-ctrl__options">
            {BRANDS.map(b => {
              const on = selected.includes(b.value);
              return (
                <button key={b.value} type="button"
                  className={`bl-ctrl__option${on ? ' bl-ctrl__option--selected' : ''}`}
                  onClick={() => toggle(b.value)} role="option" aria-selected={on}>
                  <span className={`bl-ctrl__check${on ? ' bl-ctrl__check--on' : ''}`}>{on && <Check size={10} weight="bold" color="white" />}</span>
                  {b.label}
                </button>
              );
            })}
          </div>
          <div className="bl-ctrl__footer">
            <button type="button" className="bl-ctrl__clear" onClick={() => onChange([])}>Clear all</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function PostsClientPage({ posts, searchPosts, variant = 'innovaccer' }: Props) {
  const isFlow = variant === 'flow';
  const isComet = variant === 'comet';
  const isGalaxy = variant === 'galaxy';
  const isGravity = variant === 'gravity';
  const isStoryhealth = variant === 'storyhealth';
  const isBrand = isFlow || isComet || isGalaxy || isGravity || isStoryhealth;
  const basePath = isFlow ? '/flow/blogs' : isComet ? '/comet/blogs' : isGalaxy ? '/galaxy/blogs' : isGravity ? '/gravity/blogs' : isStoryhealth ? '/storyhealth/resources' : '/blogs';
  const allowedBrands = isFlow ? new Set(['flow']) : isComet ? new Set(['comet']) : isGalaxy ? new Set(['galaxy']) : isGravity ? new Set(['gravity']) : isStoryhealth ? new Set(['storyhealth']) : VISIBLE_BRANDS;
  // Story Health gets an extra "Case Study" tab (to the right of Conversations) — other brands keep the fixed set.
  const allCategories = isStoryhealth ? ADMIN_STYLE_TABS : EDITORIAL_CATEGORIES;
  const router = useRouter();
  const searchParams = useSearchParams();
  const initialQ = searchParams?.get('q') ?? '';
  const initialCategory = SLUG_TO_CATEGORY[searchParams?.get('category') ?? ''] ?? 'All';
  const initialBrand = searchParams?.get('brand') ?? '';
  const [activeCategory, setActiveCategory] = useState(
    allCategories.includes(initialCategory) ? initialCategory : 'All'
  );
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest');
  const [filterTopics, setFilterTopics] = useState<string[]>([]);
  const [filterBrand, setFilterBrand] = useState<string[]>(
    initialBrand && BRANDS.some(b => b.value === initialBrand) ? [initialBrand] : []
  );
  const [searchQuery, setSearchQuery] = useState(initialQ);

  useEffect(() => {
    const cat = SLUG_TO_CATEGORY[searchParams?.get('category') ?? ''] ?? 'All';
    setActiveCategory(allCategories.includes(cat) ? cat : 'All');
    const q = searchParams?.get('q') ?? '';
    setSearchQuery(q);
    const brand = searchParams?.get('brand') ?? '';
    setFilterBrand(brand ? brand.split(',').filter(v => BRANDS.some(b => b.value === v)) : []);
  }, [searchParams]);

  const handleCategoryClick = (cat: string) => {
    setActiveCategory(cat);
    const slug = CATEGORY_TO_SLUG[cat] ?? '';
    const params = new URLSearchParams();
    if (slug) params.set('category', slug);
    if (searchQuery.trim()) params.set('q', searchQuery.trim());
    if (filterBrand.length) params.set('brand', filterBrand.join(','));
    const qs = params.toString();
    router.push(`${basePath}${qs ? `?${qs}` : ''}`, { scroll: false });
  };

  const handleBrandChange = (brands: string[]) => {
    setFilterBrand(brands);
    setVisibleCount(PAGE_SIZE);
    const params = new URLSearchParams();
    const slug = CATEGORY_TO_SLUG[activeCategory] ?? '';
    if (slug) params.set('category', slug);
    if (searchQuery.trim()) params.set('q', searchQuery.trim());
    if (brands.length) params.set('brand', brands.join(','));
    const qs = params.toString();
    router.push(`${basePath}${qs ? `?${qs}` : ''}`, { scroll: false });
  };

  // Filter dropdown list: the full canonical blog topic taxonomy, always offered
  // regardless of whether a post currently uses it yet (e.g. a newly added topic).
  const allRawCategories = CANONICAL_CATEGORIES;

  // How many posts live under each section tab for this brand — used only to
  // decide whether a tab is worth showing at all. 'All' always shows even
  // when the whole page is empty.
  const tabCounts = useMemo(() => {
    const brandPosts = posts.filter(p => allowedBrands.has(p.brand || 'innovaccer'));
    const counts: Record<string, number> = { All: brandPosts.length };
    for (const cat of allCategories) if (cat !== 'All') counts[cat] = 0;
    for (const p of brandPosts) {
      const tab = isStoryhealth ? adminStyleTabFor(p) : toEditorial(p.category || '', p.contentType);
      if (tab in counts) counts[tab]++;
    }
    return counts;
  }, [posts, allowedBrands, allCategories, isStoryhealth]);
  const visibleCategories = useMemo(
    () => allCategories.filter(cat => cat === 'All' || tabCounts[cat] > 0),
    [allCategories, tabCounts],
  );

  // If the active tab disappears (no posts left under it), fall back to "All"
  // instead of showing a dead, unselectable tab with an empty state.
  useEffect(() => {
    if (!visibleCategories.includes(activeCategory)) setActiveCategory('All');
  }, [visibleCategories, activeCategory]);

  // Search + filters applied to the same list — search always scoped to active category
  const filtered = useMemo(() => {
    // Category filter always applied first — search operates within the active category
    let list = (activeCategory === 'All'
      ? posts
      : isStoryhealth
        ? posts.filter((p) => adminStyleTabFor(p) === activeCategory)
        : posts.filter((p) => toEditorial(p.category || '', p.contentType) === activeCategory)
    ).filter(p => allowedBrands.has(p.brand || 'innovaccer'));
    if (filterTopics.length > 0) {
      list = list.filter((p) => p.category && filterTopics.includes(p.category));
    }
    if (filterBrand.length > 0) {
      list = list.filter((p) => filterBrand.includes(p.brand || 'innovaccer'));
    }
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      list = list.filter(
        (p) =>
          p.title.toLowerCase().includes(q) ||
          (p.dek || '').toLowerCase().includes(q) ||
          (p.category || '').toLowerCase().includes(q) ||
          (p.contentType || '').toLowerCase().includes(q) ||
          (p.tags ?? []).some((t) => t.toLowerCase().includes(q)),
      );
    }
    return [...list].sort((a, b) => {
      const da = a.date ? new Date(a.date).getTime() : 0;
      const db = b.date ? new Date(b.date).getTime() : 0;
      return sortOrder === 'newest' ? db - da : da - db;
    });
  }, [activeCategory, sortOrder, filterTopics, filterBrand, searchQuery, posts]);

  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  useEffect(() => { setVisibleCount(PAGE_SIZE); }, [activeCategory, filterTopics, filterBrand, sortOrder, searchQuery]);

  return (
    <ErrorBoundary>
      <div className="bl-page">
        <a href="#bl-main-content" className="bl-sr-only">Skip to main content</a>

        {isFlow
          ? <FlowNavBar />
          : isComet
          ? <CometNavBar />
          : isGalaxy
          ? <GalaxyNavBar />
          : isGravity
          ? <GravityNavBar />
          : isStoryhealth
          ? <StoryhealthNavBar />
          : <NavBarMain ctaLabel="REQUEST A DEMO" ctaHref="https://innovaccer.com/request-a-demo" storiesHref="/blogs" />}
        <main id="bl-main-content">
          {/* ── Hero: always visible ────────────────────────────────────── */}
          <section
            className="section-x-pad"
            style={{
              background: 'var(--seasons-color-surface-default)',
              paddingTop: 'var(--seasons-space-12)',
              paddingBottom: 'var(--seasons-space-8)',
            }}
          >
            <motion.div className="bl-page-header" initial="hidden" animate="visible" variants={stagger}>
              <motion.h1
                variants={fadeUp}
                className="type-display-xl"
                style={{ color: 'var(--seasons-color-prose-heading)', marginBottom: 0, maxWidth: 'none' }}
              >
                {isStoryhealth ? 'Resources' : 'Stories'}
              </motion.h1>
              <motion.p variants={fadeUp} className="type-body-lg bl-page-header__sub">
                {isStoryhealth
                  ? "Explore blogs, case studies, and updates from Story Health's work in chronic care."
                  : "Explore the people, progress, and perspectives behind Innovaccer's mission to make healthcare autonomous."}
              </motion.p>
              {/* Search bar — always mounted */}
              <motion.div variants={fadeUp} className="webinars-search webinars-search--hero">
                <svg width="18" height="18" viewBox="0 0 16 16" fill="none" aria-hidden="true" className="webinars-search__icon">
                  <circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.5" />
                  <path d="M10.5 10.5L13.5 13.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
                <input
                  type="search"
                  className="webinars-search__input"
                  placeholder="Search stories"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  aria-label="Search stories"
                />
                {searchQuery && (
                  <button className="webinars-search__clear" onClick={() => setSearchQuery('')} aria-label="Clear search">
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor" aria-hidden="true">
                      <path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                  </button>
                )}
              </motion.div>
            </motion.div>
          </section>

          {/* ── Filter bar: always visible ──────────────────────────────── */}
          <div className="bl-filter-bar section-x-pad" style={{ paddingTop: 0, paddingBottom: 0, overflow: 'visible' }}>
            <div className="bl-fb-bar">
              <div role="group" aria-label="Filter by category" className="bl-fb-v2 bl-fb-v2--bare">
                {visibleCategories.map((cat) => (
                  <button
                    key={cat}
                    type="button"
                    className={`bl-fb-v2__tab${activeCategory === cat ? ' bl-fb-v2__tab--active' : ''}`}
                    onClick={() => handleCategoryClick(cat)}
                    aria-pressed={activeCategory === cat}
                  >
                    {cat}
                  </button>
                ))}
              </div>
              <div className="bl-fb-controls">
                {!isBrand && <BrandDropdown selected={filterBrand} onChange={handleBrandChange} />}
                {/* Filter-by-topic doesn't apply yet for Story Health (no canonical topic
                    taxonomy assigned to its posts) — hide rather than show an empty panel. */}
                {!isStoryhealth && <FilterDropdown topics={allRawCategories} selected={filterTopics} onChange={setFilterTopics} />}
                <SortDropdown value={sortOrder} onChange={setSortOrder} />
              </div>
            </div>
          </div>

          {/* ── Results grid: always visible, filtered by search + tabs ─── */}
          <section
            className="section-x-pad"
            style={{
              background: 'var(--seasons-color-surface-subtle)',
              paddingTop: 'var(--seasons-space-12)',
              paddingBottom: 'var(--seasons-space-20)',
            }}
          >
            {searchQuery.trim() && (
              <p className="type-body-md" style={{ color: 'var(--seasons-color-text-secondary)', marginBottom: 'var(--seasons-space-6)' }}>
                {filtered.length === 0
                  ? `No results for "${searchQuery.trim()}"`
                  : `${filtered.length} result${filtered.length !== 1 ? 's' : ''} for "${searchQuery.trim()}"`}
              </p>
            )}
            {filtered.slice(0, visibleCount).length === 0 ? (
              <div className="bl-no-results">
                <p className="type-heading-md" style={{ color: 'var(--seasons-color-prose-heading)', margin: 0 }}>
                  {searchQuery.trim() ? 'No stories match your search' : `No stories in ${activeCategory}`}
                </p>
                <p className="type-body-md" style={{ color: 'var(--seasons-color-text-secondary)', margin: 0 }}>
                  {searchQuery.trim() ? 'Try different keywords or clear the search.' : 'Check back soon — more coming.'}
                </p>
              </div>
            ) : (
              <>
                <motion.div className="bl-grid" initial="hidden" animate="visible" variants={stagger}>
                  {filtered.slice(0, visibleCount).map((post) => (
                    <PostCardStandard key={post.slug} post={post} />
                  ))}
                </motion.div>
                {filtered.length > visibleCount && (
                  <div className="bl-load-more">
                    <p className="bl-load-count" aria-live="polite" aria-atomic="true">
                      Showing {Math.min(visibleCount, filtered.length)} of {filtered.length} stories
                    </p>
                    <SimpleButton
                      label="Load more"
                      size="lg"
                      variant="outline"
                      onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                      textSwap
                    />
                  </div>
                )}
              </>
            )}
          </section>
        </main>

        {isFlow ? <FlowFooter /> : isComet ? <CometFooter /> : isGalaxy ? <GalaxyFooter /> : isGravity ? <GravityFooter /> : isStoryhealth ? <StoryhealthFooter /> : <Footer theme="light" />}
      </div>
    </ErrorBoundary>
  );
}
